import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Send, Clock } from 'lucide-react';
import api from '../services/api';

interface EmailVerificationProps {
  email: string;
  isVerified: boolean;
  onVerificationComplete: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  isVerified,
  onVerificationComplete,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [status, setStatus] = useState<{
    emailVerified: boolean;
    canResend: boolean;
  }>({
    emailVerified: isVerified,
    canResend: true,
  });

  useEffect(() => {
    setStatus((prev) => ({ ...prev, emailVerified: isVerified }));
  }, [isVerified]);

  const handleSendVerification = async () => {
    setIsSending(true);
    setMessage(null);

    try {
      await api.sendEmailVerification(email);
      setMessage({
        type: 'success',
        text: 'Verification code sent to your email!',
      });
      setStatus((prev) => ({ ...prev, canResend: false }));

      // Allow resend after 60 seconds
      setTimeout(() => {
        setStatus((prev) => ({ ...prev, canResend: true }));
      }, 60000);
    } catch (error: any) {
      console.error('Error sending email verification:', error);
      let errorMessage = 'Failed to send verification code. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setMessage({
        type: 'error',
        text: 'Please enter the verification code.',
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await api.verifyEmailCode(verificationCode);
      if (result.verified) {
        setMessage({
          type: 'success',
          text: 'Email verified successfully!',
        });
        setStatus((prev) => ({ ...prev, emailVerified: true }));
        setVerificationCode('');
        onVerificationComplete();
      }
    } catch (error: any) {
      console.error('Error verifying email code:', error);
      let errorMessage = 'Failed to verify code. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  if (status.emailVerified) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <div className="flex items-center">
          <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-green-800">
              Email Verified
            </h3>
            <p className="text-sm text-green-700 mt-1">
              Your email address has been verified successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
      <div className="flex items-center mb-4">
        <Mail className="h-5 w-5 text-gray-400 mr-2" />
        <div>
          <h3 className="text-sm font-medium text-gray-900">
            Email Verification
          </h3>
          <p className="text-sm text-gray-500">
            Verify your email address to receive important notifications.
          </p>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <p className="text-sm text-gray-900">{email}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Verification Code
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={handleInputChange}
            placeholder="Enter 6-digit code"
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            maxLength={6}
          />
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleVerifyCode}
            disabled={isLoading || !verificationCode.trim()}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </button>

          <button
            onClick={handleSendVerification}
            disabled={isSending || !status.canResend}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Sending...
              </>
            ) : !status.canResend ? (
              <>
                <Clock className="h-4 w-4 mr-2" />
                Wait 60s
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Code
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
